import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Grid, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert,
  Box, Chip, LinearProgress, Avatar
} from '@mui/material';
import {
  People, DriveEta, CalendarToday, Feedback,
  Search, CheckCircle, DirectionsCar, LocationOn, Build, Block,
  HourglassEmpty, TrendingUp, Storefront
} from '@mui/icons-material';

const GET_STATUS_CONFIG = (t) => ({
  searching_driver: { label: t('searching'), color: '#f59e0b', bg: '#fef3c7', icon: <Search sx={{ fontSize: 15 }} /> },
  confirmed: { label: t('assigned'), color: '#3b82f6', bg: '#dbeafe', icon: <DirectionsCar sx={{ fontSize: 15 }} /> },
  driver_arrived: { label: t('arrived'), color: '#8b5cf6', bg: '#ede9fe', icon: <LocationOn sx={{ fontSize: 15 }} /> },
  in_progress: { label: t('inProgress'), color: '#ec4899', bg: '#fce7f3', icon: <Build sx={{ fontSize: 15 }} /> },
  completed: { label: t('completed'), color: '#10b981', bg: '#d1fae5', icon: <CheckCircle sx={{ fontSize: 15 }} /> },
  cancelled: { label: t('cancelled'), color: '#ef4444', bg: '#fee2e2', icon: <Block sx={{ fontSize: 15 }} /> },
  pending: { label: t('pending'), color: '#64748b', bg: '#f1f5f9', icon: <HourglassEmpty sx={{ fontSize: 15 }} /> },
});

const StatusChip = ({ status, t }) => {
  const cfg = GET_STATUS_CONFIG(t)[status] || { label: status, color: '#888', bg: '#f0f0f0', icon: null };
  return (
    <Chip
      size="small"
      icon={cfg.icon}
      label={cfg.label}
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 700,
        fontSize: '0.72rem',
        border: `1px solid ${cfg.color}33`,
        '& .MuiChip-icon': { color: cfg.color },
      }}
    />
  );
};

const STAT_CARDS = (stats, t) => [
  {
    label: t('totalOwners'),
    value: stats?.ownerCount || 0,
    icon: <Storefront />,
    gradient: 'linear-gradient(135deg, #1a7e1aff 0%, #12c03d 100%)',
    shadow: 'rgba(17,102,17,0.35)',
    bg: '#e8f5e9',
  },
  {
    label: t('totalDrivers'),
    value: stats?.driverCount || 0,
    icon: <People />,
    gradient: 'linear-gradient(135deg, #116611 0%, #338800 100%)',
    shadow: 'rgba(17,102,17,0.35)',
    bg: '#e8f5e9',
  },
  {
    label: t('totalVehicles'),
    value: stats?.vehicleCount || 0,
    icon: <DriveEta />,
    gradient: 'linear-gradient(135deg, #338800 0%, #55AA22 100%)',
    shadow: 'rgba(51,136,0,0.35)',
    bg: '#f1f8e9',
  },
  {
    label: t('totalBookings'),
    value: stats?.bookingCount || 0,
    icon: <CalendarToday />,
    gradient: 'linear-gradient(135deg, #0a400a 0%, #116611 100%)',
    shadow: 'rgba(17,102,17,0.35)',
    bg: '#e8f5e9',
  },
  {
    label: 'Total Feedbacks',
    value: stats?.feedbackCount || 0,
    icon: <Feedback />,
    gradient: 'linear-gradient(135deg, #55AA22 0%, #99CC77 100%)',
    shadow: 'rgba(85,170,34,0.35)',
    bg: '#f1f8e9',
  },
];

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/admin/stats');
        setStats(response.data);
      } catch (err) {
        setError(err.response?.data?.message || t('error'));
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [t]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: '#1a237e' }} />
    </Box>
  );
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;

  const totalBookings = stats?.bookingCount || 0;
  const bbs = stats?.bookingByStatus || {};

  // Current time greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('goodMorning') : hour < 17 ? t('goodAfternoon') : t('goodEvening');

  return (
    <Box className="page-enter">
      {/* ── Page Header ── */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} sx={{ color: '#1a1a2e', mb: 0.5 }}>
          {greeting}, Admin 👋
        </Typography>
        <Typography sx={{ color: '#78909c', fontSize: '0.9rem' }}>
          {t('dashboardSubtitle')}
        </Typography>
      </Box>

      {/* ── STAT CARDS ── */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {STAT_CARDS(stats, t).map((card, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <Card
              sx={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                overflow: 'visible',
                position: 'relative',
                bgcolor: '#ffffff',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar
                    sx={{
                      background: card.gradient,
                      width: 48,
                      height: 48,
                      borderRadius: '14px',
                      boxShadow: `0 6px 16px ${card.shadow}`,
                      '& svg': { fontSize: '1.4rem', color: '#ffffff' },
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box sx={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <TrendingUp sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981' }}>Active</Typography>
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={900} sx={{ color: '#1a1a2e', lineHeight: 1, mb: 0.5 }}>
                  {card.value}
                </Typography>
                <Typography sx={{ color: '#78909c', fontSize: '0.82rem', fontWeight: 500 }}>
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── BOOKING STATUS BREAKDOWN ── */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a2e' }}>
          {t('bookingStatusBreakdown')}
        </Typography>
        <Chip label="Real-time" size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: '0.7rem' }} />
      </Box>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {Object.entries(GET_STATUS_CONFIG(t)).map(([key, cfg]) => {
          const count = bbs[key] || 0;
          const pct = totalBookings > 0 ? Math.round((count / totalBookings) * 100) : 0;
          return (
            <Grid item xs={6} sm={4} md={3} key={key}>
              <Card sx={{ borderRadius: '14px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: `1px solid ${cfg.color}20` }}>
                <CardContent sx={{ pb: '14px !important', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                    <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon}</Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {cfg.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={900} sx={{ color: '#1a1a2e', mb: 1 }}>{count}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 5,
                      borderRadius: 10,
                      bgcolor: `${cfg.color}18`,
                      '& .MuiLinearProgress-bar': { bgcolor: cfg.color, borderRadius: 10 },
                    }}
                  />
                  <Typography sx={{ fontSize: '0.7rem', color: '#90a4ae', mt: 0.5 }}>{pct}% of total</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── RECENT BOOKINGS TABLE ── */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a2e' }}>
          {t('recentBookings')}
        </Typography>
      </Box>

      {stats?.recentBookings?.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(90deg, #1a237e 0%, #3949ab 100%)',
                }}
              >
                {['Customer', 'Vehicle No.', 'Type', 'Amount', 'Date', 'Status'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 1.8,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.recentBookings.map((booking, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    bgcolor: index % 2 === 0 ? '#ffffff' : '#f3faf3',
                    '&:hover': { bgcolor: '#e8f5e9' },
                    transition: 'background-color 0.15s ease',
                    '& .MuiTableCell-root': { borderColor: '#f1f5f9', py: 1.5 },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: '#e8f5e9', color: '#338800', fontSize: '0.8rem', fontWeight: 700 }}>
                        {(booking.customer?.name || '?')[0].toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {booking.customer?.name || t('noData')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.82rem', color: '#116611', bgcolor: '#e8f5e9', px: 1, py: 0.3, borderRadius: 1, display: 'inline-block' }}>
                      {booking.vehicle?.vehicleNumber || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={booking.vehicleType || '—'} size="small" variant="outlined" sx={{ borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem', borderColor: '#cbd5e1', color: '#475569' }} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={800} sx={{ color: '#10b981', fontSize: '0.9rem' }}>
                      ₹{booking.amount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                      {booking.createdAt
                        ? new Date(booking.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell><StatusChip status={booking.status} t={t} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>{t('noData')}</Alert>
      )}
    </Box>
  );
};

export default Dashboard;